import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { COMPANY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please enter your name, email and phone.");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch {
      toast.error("Couldn't send. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 text-white placeholder:text-white/30 rounded-none transition-colors";

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="contactpage-home" className="inline-flex items-center gap-2 text-white/50 hover:text-[#F2B705] text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>

        <div className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
          <div>
            <div className="font-display font-extrabold text-2xl">AutoHire <span className="text-gold-gradient">STL</span></div>
            <div className="text-[#F2B705] font-script italic text-lg leading-none">"{COMPANY.tagline}"</div>
          </div>
        </div>

        <div className="mt-8 bg-[#0C0C0C] border border-white/10 rounded-2xl p-7" data-testid="contactpage-card">
          {done ? (
            <div className="text-center py-6" data-testid="contactpage-success">
              <CheckCircle2 className="text-[#F2B705] mx-auto" size={48} />
              <h1 className="font-display font-black text-2xl mt-4">You're In! 🎉</h1>
              <p className="text-white/60 mt-2">Thanks {form.name || "there"} — we got your info and we'll reach out shortly. Want to lock in a car now?</p>
              <Link to="/?book=1" className="inline-block mt-6 bg-[#F2B705] text-black font-bold px-8 py-3.5 rounded-md hover:bg-white transition-colors">Book a Car</Link>
            </div>
          ) : (
            <>
              <h1 className="font-display font-black text-3xl">Get In Touch</h1>
              <p className="text-white/55 mt-2">Drop your details and we'll get right back to you about availability, pricing & rideshare options.</p>

              <div className="mt-5 flex flex-wrap gap-3">
                <a href={COMPANY.phoneHref} className="flex items-center gap-2 text-sm border border-white/15 rounded-full px-4 py-2 hover:border-[#F2B705]/50 transition-colors"><Phone size={15} className="text-[#F2B705]" /> {COMPANY.phone}</a>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-sm border border-white/15 rounded-full px-4 py-2 hover:border-[#F2B705]/50 transition-colors"><Mail size={15} className="text-[#F2B705]" /> Email</a>
              </div>

              <form onSubmit={submit} className="mt-7 space-y-6" data-testid="contactpage-form">
                <input data-testid="contactpage-name" className={inputCls} placeholder="Your name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="contactpage-email" type="email" className={inputCls} placeholder="Your email *" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="contactpage-phone" type="tel" className={inputCls} placeholder="Your phone number *" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                <textarea data-testid="contactpage-message" rows={3} className={inputCls} placeholder="How can we help? (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} />

                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input data-testid="contactpage-agree" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 accent-[#F2B705] shrink-0" />
                  <span className="text-sm text-white/55">
                    I agree to the <Link to="/terms" className="text-[#F2B705] hover:underline">Terms</Link> and <Link to="/privacy" className="text-[#F2B705] hover:underline">Privacy Policy</Link>, and consent to be contacted by phone, SMS and email.
                  </span>
                </label>

                <button data-testid="contactpage-submit" type="submit" disabled={loading}
                  className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading && <Loader2 className="animate-spin" size={18} />}
                  {loading ? "Sending..." : "Send My Info"}
                </button>
              </form>
            </>
          )}
        </div>
        <p className="text-white/30 text-xs text-center mt-6">{COMPANY.legalName}</p>
      </div>
    </div>
  );
}
